import type { IconType } from "@sellar-io/components";
import { orderableItemValues } from "@sellar-io/common";
import type { ProductTemplateCreateRequestType } from "@sellar-io/api-v2/types";

export const containerCategories = [
  { key: "keg", label: "Keg" },
  { key: "cask", label: "Cask" },
  { key: "can", label: "Can" },
  { key: "bottle", label: "Bottle" },
  { key: "bagInBox", label: "Bag in box" },
  { key: "other", label: "Other" },
] as const;

export type ContainerCategory = (typeof containerCategories)[number]["key"];

export type ContainerSize = Pick<
  ProductTemplateCreateRequestType,
  "displayUnit" | "packQuantity" | "volume"
>;

export const quickContainerSizes: Record<ContainerCategory, ContainerSize[]> = {
  keg: [
    { packQuantity: 1, volume: 30, displayUnit: "L" },
    { packQuantity: 1, volume: 50, displayUnit: "L" },
    { packQuantity: 1, volume: 20, displayUnit: "L" },
  ],
  cask: [
    { packQuantity: 1, volume: 9, displayUnit: "gal" },
    { packQuantity: 1, volume: 4.5, displayUnit: "gal" },
    { packQuantity: 1, volume: 40, displayUnit: "L" },
  ],
  can: [
    { packQuantity: 12, volume: 440, displayUnit: "ml" },
    { packQuantity: 24, volume: 440, displayUnit: "ml" },
    { packQuantity: 12, volume: 330, displayUnit: "ml" },
    { packQuantity: 24, volume: 330, displayUnit: "ml" },
    { packQuantity: 6, volume: 440, displayUnit: "ml" },
  ],
  bottle: [
    { packQuantity: 12, volume: 500, displayUnit: "ml" },
    { packQuantity: 6, volume: 750, displayUnit: "ml" },
    { packQuantity: 1, volume: 70, displayUnit: "cl" },
    { packQuantity: 12, volume: 330, displayUnit: "ml" },
    { packQuantity: 8, volume: 500, displayUnit: "ml" },
  ],
  bagInBox: [
    { packQuantity: 1, volume: 20, displayUnit: "L" },
    { packQuantity: 1, volume: 10, displayUnit: "L" },
    { packQuantity: 1, volume: 5, displayUnit: "L" },
  ],
  other: [
    { packQuantity: 10, volume: 10, displayUnit: "cl" },
    { packQuantity: 140, volume: 10, displayUnit: "cl" },
    { packQuantity: 1, volume: 10, displayUnit: "cl" },
  ],
};

const primaryCategories = new Set<string>(
  containerCategories
    .filter(({ key }) => key !== "other")
    .map(({ key }) => key),
);

export const getContainerOptions = (category: ContainerCategory) =>
  orderableItemValues.filter(
    (item) =>
      item.key !== "misc" &&
      (category === "other"
        ? !primaryCategories.has(item.key) &&
          !item.itemTags.some((tag) => primaryCategories.has(tag))
        : item.key === category ||
          item.itemTags.some((tag) => tag === category)),
  );

export const getContainerCategory = (
  containerType: string,
): ContainerCategory =>
  containerCategories.find(({ key }) =>
    getContainerOptions(key).some(({ key: type }) => type === containerType),
  )?.key ?? "other";

export const getContainerSizes = (category: ContainerCategory) =>
  (category === "other"
    ? []
    : [...quickContainerSizes[category], ...additionalContainerSizes[category]]
  ).filter(
    (size, index, all) =>
      all.findIndex(
        (candidate) =>
          candidate.volume === size.volume &&
          candidate.displayUnit === size.displayUnit,
      ) === index,
  );

const additionalContainerSizes: Record<ContainerCategory, ContainerSize[]> = {
  keg: [10, 15, 25, 40].map((volume) => ({
    packQuantity: 1,
    volume,
    displayUnit: "L",
  })),
  cask: [2.25, 18, 36].map((volume) => ({
    packQuantity: 1,
    volume,
    displayUnit: "gal",
  })),
  can: [250, 355, 500, 568].map((volume) => ({
    packQuantity: 1,
    volume,
    displayUnit: "ml",
  })),
  bottle: [187, 200, 250, 355, 375, 650, 750, 1000, 1500].map((volume) => ({
    packQuantity: 1,
    volume,
    displayUnit: "ml",
  })),
  bagInBox: [3].map((volume) => ({
    packQuantity: 1,
    volume,
    displayUnit: "L",
  })),
  other: [],
};

export const containerIcons: Record<ContainerCategory, IconType> = {
  keg: "KEG",
  cask: "CASK",
  can: "CAN",
  bottle: "BOTTLE",
  bagInBox: "PACKAGE_VARIANT_CLOSED",
  other: "PACKAGE_VARIANT_CLOSED",
};
